import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router'
import { resetMcfState } from 'redux/reducers/medicare-complaint-form'
import { useDispatch, useSelector } from 'react-redux'
import { getUserLoggedInState } from 'redux/reducers/users'
import { setActiveTrap } from 'redux/reducers/modal'

const HEADER_URL = process.env.REACT_APP_CONSISTENT_HEADER_URL

export interface ConsistentHeaderProps {
  languageToggleLink?: string;
  loginUrl?: string;
  logoutUrl?: string;
  enableMessageCenter?: boolean;
  showTimeoutMessage: () => void;
}

export const ConsistentHeader: React.FC<ConsistentHeaderProps> = ({ languageToggleLink, loginUrl, logoutUrl, enableMessageCenter, showTimeoutMessage }) => {
  const [headerFiles, setHeaderFiles] = useState([])
  const { i18n } = useTranslation()
  const isLoggedIn = useSelector(getUserLoggedInState)

  useEffect(() => {
    let mounted = true

    const fetchAssets = async () => {
      try {
        const request = await axios.get(`${HEADER_URL}/asset-manifest.json`)

        const assets = request.data.entrypoints

        if (mounted) {
          setHeaderFiles(assets)
        }
      } catch (error) {
        console.error('Error fetching Consistent Header assets')
      }
    }

    fetchAssets()

    return () => { mounted = false }
  }, [setHeaderFiles])

  // Timeout configuration
  const location = useLocation()
  const history = useHistory()
  const [timeoutEnabled, setTimeoutEnabled] = useState(false)
  const dispatch = useDispatch()
  useEffect(() => {
    window.handleGlobalHeaderWarningDisplay = () => { dispatch(setActiveTrap({ isActiveTrap: false })) }
    window.handleGlobalHeaderWarningContinueAction = () => { dispatch(setActiveTrap({ isActiveTrap: true })) }
    if (isLoggedIn) {
      setTimeoutEnabled(true)
    } else {
      if (
        location.pathname.includes('medicare-complaint/step1') ||
        location.pathname.includes('medicare-complaint/step2') ||
        location.pathname.includes('medicare-complaint/step2b')
      ) {
        window.handleGlobalHeaderTimeout = () => {
          dispatch(resetMcfState())
          history.push('/medicare-complaint')
          showTimeoutMessage()
        }
        setTimeoutEnabled(true)
      } else if (location.pathname.includes('account-settings')) {
        // TODO - delete this "else if block"  once we have protected routes
        setTimeoutEnabled(true)
      } else {
        setTimeoutEnabled(false)
      }
    }

    // Cleanup
    return () => {
      window.handleGlobalHeaderWarningContinueAction = undefined
      window.handleGlobalHeaderWarningDisplay = undefined
      window.handleGlobalHeaderTimeout = undefined
    }
  }, [dispatch, location, setTimeoutEnabled, history, showTimeoutMessage, isLoggedIn])

  return (
    <>
      {/* TODO - fix ComponentWillMount error unsafe in consoles : https://github.com/nfl/react-helmet/issues/623 */}
      <Helmet>
        {headerFiles && headerFiles.map((file: string) => {
          const fileType = file.split('/')[1]
          return fileType === 'js'
            ? <script src={`${HEADER_URL}/${file}`} key={file} />
            : <link rel='stylesheet' type='text/css' href={`${HEADER_URL}/${file}`} key={file} />
        })}
      </Helmet>
      <div
        id='medicare-header-root'
        data-testid='medicare-header-root'
        lang={i18n.language}
        lang-toggle-link={languageToggleLink}
        enable-live-chat='true'
        login-url={loginUrl}
        logout-url={logoutUrl}
        enable-message-center={enableMessageCenter ? 'true' : 'false'}
        enable-timeout={timeoutEnabled ? 'true' : 'false'}
      />
    </>)
}
